import get from 'lodash/get'
import { memo, useContext, useRef, useState } from 'react'
import { HelpCircle } from 'react-feather'
import { useHistory, useLocation } from 'react-router'

import {
  Avatar,
  Desktop,
  mainColors,
  TabletOrMobile,
  useOnClickOutside,
  zIndex,
} from '@avantstay/backoffice-core'
import { IconAvantStayLogo, IconEye2 } from '@avantstay/backoffice-vectors'

import { AvatarDropdown } from '@new/__global/components/AvatarDropdown'
import { OmniSearch } from '@new/__global/components/OmniSearch'
import { OperationsNotification } from '@new/__global/components/OperationsNotification'

import { UserButton } from '@clerk/react'
import AuthContext, { UserProfile } from '@contexts/Auth/AuthContext'
import { getMainMenuItems, mountRouteUrl, ROUTES } from '@routes'
import { ToggleFeature } from '@utils/optimizely'

import { DevEnvBadge } from './DevEnvBadge'
import * as S from './Header.styles'
import { isActiveRoute } from './Header.utils'
import { MobileMenu } from './MobileMenu'

const runAsEmail = window.localStorage.getItem('runAs')
const runAsInitials = runAsEmail?.slice(0, 2)

const _Header = () => {
  const [showAvatarDropdown, setShowAvatarDropdown] = useState<boolean>(false)
  const avatarDropdownRef = useRef<HTMLButtonElement | null>(null)
  const history = useHistory()
  const location = useLocation()

  const auth = useContext(AuthContext)
  const user = auth.user as UserProfile

  const items = getMainMenuItems(user)

  useOnClickOutside(avatarDropdownRef, () => setShowAvatarDropdown(false))

  return (
    <S.Root>
      <S.HeaderGroupLogo style={{ zIndex: zIndex.FOLD_OUT_MENU }}>
        <TabletOrMobile>
          <MobileMenu items={items} />
        </TabletOrMobile>

        <Desktop>
          <S.LogoNavLink to={ROUTES.dashboard.path}>
            <IconAvantStayLogo />
          </S.LogoNavLink>

          <DevEnvBadge />
        </Desktop>
      </S.HeaderGroupLogo>

      <Desktop>
        <S.HeaderGroupDesktopMenu>
          <S.HeaderNav>
            {items.map((route, key) => {
              const Icon = ROUTES[route]?.icon
              const externalUrl = ROUTES[route]?.externalUrl

              if (externalUrl) {
                return (
                  <S.HeaderExternalLink
                    key={key}
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-cy={`header-${ROUTES[route].title}`}
                  >
                    {Icon && <Icon width={20} height={20} />}
                    {ROUTES[route].title}
                  </S.HeaderExternalLink>
                )
              }

              return (
                <ToggleFeature key={key} feature={get(ROUTES[route], 'feature', undefined)}>
                  <S.HeaderLink
                    activeClassName="active"
                    to={mountRouteUrl(ROUTES[route]?.path)}
                    data-cy={`header-${ROUTES[route]?.title}`}
                    $isActive={isActiveRoute(location.pathname, route)}
                  >
                    {Icon && <Icon width={20} height={20} />}
                    {ROUTES[route]?.title}
                  </S.HeaderLink>
                </ToggleFeature>
              )
            })}
          </S.HeaderNav>
        </S.HeaderGroupDesktopMenu>
      </Desktop>

      <S.HeaderGroupSearch>
        <OmniSearch />
        <OperationsNotification />

        <S.HeaderAvatarContainer>
          {runAsEmail ? (
            <S.ClickableAvatar
              ref={avatarDropdownRef}
              onClick={() => setShowAvatarDropdown(prevState => !prevState)}
            >
              <Avatar
                initials={runAsInitials}
                bgColor={mainColors.neutralMedium}
                badge={<IconEye2 />}
                badgeOnTop
                viewAsBorder
                initialsColor={mainColors.primaryDark}
              />
              {showAvatarDropdown && (
                <AvatarDropdown
                  userPhoto={user.photo}
                  userName={user.name}
                  userEmail={user.email}
                  signOut={auth.signOut}
                  onHelp={() => {
                    setShowAvatarDropdown(false)
                    history.push(mountRouteUrl(ROUTES.help.path))
                  }}
                />
              )}
            </S.ClickableAvatar>
          ) : (
            // Core-3 removed UserButton's afterSignOutUrl; ClerkProvider sets it
            <UserButton>
              <UserButton.MenuItems>
                <UserButton.Action
                  label="Help"
                  labelIcon={<HelpCircle size={16} />}
                  onClick={() => history.push(mountRouteUrl(ROUTES.help.path))}
                />
              </UserButton.MenuItems>
            </UserButton>
          )}
        </S.HeaderAvatarContainer>
      </S.HeaderGroupSearch>
    </S.Root>
  )
}

export const Header = memo(_Header)
